import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { UserTableActions } from "./UserTableActions";

import UserService from "services/User";
import { formatError } from "helpers/formatError";
import { invalidateUsersQuery } from "hooks/useUsersTable";

// Constants
const COLUMN_KEYS = {
  SELECT: "select",
  ID: "id",
  FULL_NAME: "full_name",
  EMAIL: "email",
  STATUS: "status",
  SSO_BYPASS: "sso_bypass",
  ROLE: "role",
  REGION: "region",
  TITLE: "title",
  PHONE_NUMBERS: "phone_numbers",
  ACTIONS: "actions",
};

const COLUMN_IDS = {
  SELECT: SELECT_COLUMN_ID,
  ID: "ID",
  FULL_NAME: "Name",
  EMAIL: "Email",
  STATUS: "Status",
  SSO_BYPASS: "SSO Bypass",
  ROLE: "Role",
  REGION: "Region",
  TITLE: "Title",
  PHONE_NUMBERS: "Phone numbers",
  ACTIONS: "Actions",
};

const FILTER_KEYS = {
  FULL_NAME: "full_name",
  EMAIL: "email",
  TITLE: "title",
  ROLE_ID: "role_id",
  REGION_ID: "region_id",
  STATUS: "status",
};

const FILTER_TITLES = {
  FULL_NAME: "Name",
  EMAIL: "Email",
  TITLE: "Title",
  ROLE: "Role",
  REGION: "Region",
  STATUS: "Status",
};

export const getUsersTableColumns = ({
  canEditUser = false,
  canDeleteUser = false,
  canResetPassword = false,
  canDeactivateUser = false,
  onEditUser = () => {},
  onDeleteUser = () => {},
  onResetPassword = () => {},
  onDeactivateUser = () => {},
  onReactivateUser = () => {},
  onForceLogout = () => {},
}) => {
  const columns = [
    {
      id: COLUMN_IDS.SELECT,
      size: 60,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      header: ({ table }) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onCheckedChange: checked => table.toggleAllRowsSelected(!!checked),
            "aria-label": "Select all rows",
          }}
        />
      ),
      cell: ({ row }) => (
        <div>
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected() && !row.getIsSelected(),
              onCheckedChange: checked => row.toggleSelected(!!checked),
              "aria-label": "Select row",
            }}
          />
        </div>
      ),
    },
    {
      header: COLUMN_IDS.ID,
      id: COLUMN_IDS.ID,
      accessorKey: COLUMN_KEYS.ID,
      size: 50,
      cell: ({ row }) => (
        <p style={{ fontWeight: "bold" }}>{row.getValue(COLUMN_IDS.ID)}</p>
      ),
    },
    {
      header: COLUMN_IDS.FULL_NAME,
      id: COLUMN_IDS.FULL_NAME,
      accessorKey: COLUMN_KEYS.FULL_NAME,
      cell: ({ row }) => <div>{row.getValue(COLUMN_IDS.FULL_NAME)}</div>,
    },
    {
      header: COLUMN_IDS.EMAIL,
      id: COLUMN_IDS.EMAIL,
      accessorKey: COLUMN_KEYS.EMAIL,
      cell: ({ row }) => <div>{row.getValue(COLUMN_IDS.EMAIL)}</div>,
    },
    {
      header: COLUMN_IDS.STATUS,
      id: COLUMN_IDS.STATUS,
      accessorKey: COLUMN_KEYS.STATUS,
      size: 100,
      cell: ({ row }) => {
        const status = row.getValue(COLUMN_IDS.STATUS);
        const isActive = status === "active" || !status;
        return (
          <Badge variant={isActive ? "success" : "secondary"}>
            {isActive ? "Active" : "Deactivated"}
          </Badge>
        );
      },
    },
    {
      header: COLUMN_IDS.SSO_BYPASS,
      id: COLUMN_IDS.SSO_BYPASS,
      accessorKey: COLUMN_KEYS.SSO_BYPASS,
      size: 100,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;

        return (
          <Switch
            checked={user.sso_bypass || false}
            disabled={!canEditUser}
            onCheckedChange={async checked => {
              try {
                // The update endpoint resets every field missing from the
                // payload, so the toggle resends the whole stored record.
                await UserService.updateUser(user.id, {
                  email: user.email,
                  fullName: user.full_name,
                  title: user.title,
                  phoneNumbers: user.phone_numbers,
                  role: user.role.id,
                  region: user.region_id,
                  groupId: user.group_id,
                  customFields: user.custom_fields,
                  ssoBypass: checked,
                });
                await invalidateUsersQuery();
              } catch (error) {
                toast.error(formatError(error, "Failed to update SSO bypass"));
              }
            }}
            aria-label="Toggle SSO bypass"
          />
        );
      },
    },
    {
      header: COLUMN_IDS.ROLE,
      id: COLUMN_IDS.ROLE,
      accessorKey: COLUMN_KEYS.ROLE,
      cell: ({ row }) => {
        const role = row.getValue(COLUMN_IDS.ROLE);
        return <div>{role?.name || null}</div>;
      },
    },
    {
      header: COLUMN_IDS.REGION,
      id: COLUMN_IDS.REGION,
      accessorKey: COLUMN_KEYS.REGION,
      cell: ({ row }) => <div>{row.getValue(COLUMN_IDS.REGION)}</div>,
    },
    {
      header: COLUMN_IDS.TITLE,
      id: COLUMN_IDS.TITLE,
      accessorKey: COLUMN_KEYS.TITLE,
      cell: ({ row }) => <div>{row.getValue(COLUMN_IDS.TITLE)}</div>,
    },
    {
      header: COLUMN_IDS.PHONE_NUMBERS,
      id: COLUMN_IDS.PHONE_NUMBERS,
      accessorKey: COLUMN_KEYS.PHONE_NUMBERS,
      enableSorting: false,
      cell: ({ row }) => {
        const phoneNumbers = row.getValue(COLUMN_IDS.PHONE_NUMBERS) || [];
        const sortedPhoneNumbers = [...phoneNumbers].sort((a, b) =>
          a.is_primary ? -1 : b.is_primary ? 1 : 0
        );
        return (
          <ScrollColumn>
            {sortedPhoneNumbers.map(phone => (
              <li key={phone.id}>
                {phone.ext ? `${phone.number}x${phone.ext}` : phone.number}
              </li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: null,
      id: COLUMN_IDS.ACTIONS,
      accessorKey: COLUMN_KEYS.ACTIONS,
      size: 60,
      enableSorting: false,
      enableResizing: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <UserTableActions
          user={row.original}
          canEditUser={canEditUser}
          canDeleteUser={canDeleteUser}
          canResetPassword={canResetPassword}
          canDeactivateUser={canDeactivateUser}
          onEditUser={onEditUser}
          onDeleteUser={onDeleteUser}
          onResetPassword={onResetPassword}
          onDeactivateUser={onDeactivateUser}
          onReactivateUser={onReactivateUser}
          onForceLogout={onForceLogout}
        />
      ),
    },
  ];

  return columns;
};

export const getUsersTableFilters = ({ roles = [], regions = [] }) => {
  return [
    {
      key: FILTER_KEYS.FULL_NAME,
      title: FILTER_TITLES.FULL_NAME,
      type: "input",
    },
    {
      key: FILTER_KEYS.EMAIL,
      title: FILTER_TITLES.EMAIL,
      type: "input",
    },
    {
      key: FILTER_KEYS.TITLE,
      title: FILTER_TITLES.TITLE,
      type: "input",
    },
    {
      key: FILTER_KEYS.ROLE_ID,
      title: FILTER_TITLES.ROLE,
      type: "select",
      data: roles,
    },
    {
      key: FILTER_KEYS.REGION_ID,
      title: FILTER_TITLES.REGION,
      type: "select",
      data: regions,
    },
    {
      key: FILTER_KEYS.STATUS,
      title: FILTER_TITLES.STATUS,
      type: "select",
      data: [
        { id: "active", name: "Active" },
        { id: "deactivated", name: "Deactivated" },
      ],
    },
  ];
};
